/**
 * Permission catalog. Keep this file as the single source of truth for both
 * the API seed and the Web navigation. Codes are stable; display names may
 * change without breaking stored grants.
 */

export type PermissionGroup =
  | "菜单"
  | "系统管理"
  | "资产"
  | "物料"
  | "库存"
  | "业务办理"
  | "维修"
  | "软件许可"
  | "标签"
  | "报表"
  | "提醒"
  | "审计"
  | "附件";

export interface PermissionDefinition {
  code: string;
  name: string;
  group: PermissionGroup;
  description?: string;
}

const menu = (code: string, name: string): PermissionDefinition => ({
  code: `menu:${code}`,
  name,
  group: "菜单",
});

export const PERMISSIONS = [
  menu("dashboard", "工作台"),
  menu("materials", "物料管理"),
  menu("assets", "资产管理"),
  menu("accessories", "低值易耗"),
  menu("licenses", "软件许可"),
  menu("approvals", "待办审批"),
  menu("requests", "业务办理"),
  menu("handovers", "交接确认"),
  menu("inventory", "库存视图"),
  menu("label_tasks", "标签任务"),
  menu("maintenance", "维修维保"),
  menu("reports", "统计报表"),
  menu("audit", "审计日志"),
  menu("reminders", "提醒中心"),
  menu("attachments", "附件管理"),
  menu("admin_users", "用户管理"),
  menu("admin_personnel", "人员基础数据"),
  menu("admin_access", "角色与授权"),
  menu("admin_master_data", "主数据配置"),
  menu("admin_basic_data", "基础数据管理"),
  menu("admin_label_settings", "标签打印配置"),
  menu("admin_code_rules", "编号规则配置"),
  menu("admin_source_whitelist", "源白名单"),
  menu("admin_special_actions", "特殊情况处理"),

  { code: "admin:access:manage", name: "角色与授权管理", group: "系统管理" },
  { code: "admin:users:manage", name: "用户管理", group: "系统管理" },
  { code: "admin:personnel:manage", name: "人员管理", group: "系统管理" },
  { code: "admin:master-data:manage", name: "主数据配置", group: "系统管理" },
  { code: "admin:basic-data:manage", name: "基础数据管理", group: "系统管理" },
  { code: "admin:code-rules:manage", name: "编号规则管理", group: "系统管理" },
  { code: "admin:label-settings:manage", name: "标签设置管理", group: "系统管理" },
  { code: "admin:source-whitelist:manage", name: "源白名单管理", group: "系统管理" },
  { code: "admin:special:manage", name: "特殊情况处理", group: "系统管理" },
  { code: "admin:audit:read", name: "审计读取", group: "系统管理" },
  { code: "admin:system:read", name: "系统状态读取", group: "系统管理" },

  { code: "asset:manage", name: "资产管理", group: "资产" },
  { code: "asset:view_all", name: "查看全部资产", group: "资产" },
  { code: "asset:view_self", name: "查看本人资产", group: "资产" },
  { code: "asset:accept", name: "资产验收", group: "资产" },
  { code: "asset:dispose", name: "资产处置", group: "资产" },

  { code: "material:manage", name: "物料管理", group: "物料" },
  { code: "material:classification:override", name: "分类越阈值调整", group: "物料" },
  { code: "taxonomy:manage", name: "物料层级维护", group: "物料" },

  { code: "inventory:manage", name: "库存管理", group: "库存" },
  { code: "stock:correct", name: "库存冲正", group: "库存" },

  { code: "request:manage", name: "业务办理", group: "业务办理" },
  { code: "request:approve", name: "审批申领", group: "业务办理" },
  { code: "request:approve_on_behalf", name: "代审批", group: "业务办理" },
  { code: "request:asset:requisition", name: "申领资产", group: "业务办理" },
  { code: "request:asset:replacement", name: "申请更新设备", group: "业务办理" },
  { code: "request:asset:return", name: "申请退回设备", group: "业务办理" },
  { code: "request:asset:repair", name: "申请维修设备", group: "业务办理" },
  { code: "request:asset:loss", name: "报告遗失", group: "业务办理" },
  { code: "request:asset:borrow", name: "借用设备", group: "业务办理" },
  { code: "request:software:seat", name: "申请软件席位", group: "业务办理" },
  { code: "request:software:renewal", name: "申请软件续期", group: "业务办理" },

  { code: "operation:manage", name: "维修与运营管理", group: "维修" },
  { code: "maintenance:approve", name: "维修成本审批", group: "维修" },

  { code: "license:manage", name: "软件许可管理", group: "软件许可" },
  { code: "license:seat:manage", name: "许可席位管理", group: "软件许可" },
  { code: "license:renew", name: "许可续期", group: "软件许可" },

  { code: "label:manage", name: "标签任务管理", group: "标签" },
  { code: "report:read", name: "报表读取", group: "报表" },
  { code: "reminder:read", name: "提醒读取", group: "提醒" },
  { code: "audit:read", name: "审计读取", group: "审计" },
  { code: "attachment:manage", name: "附件管理", group: "附件" },
  { code: "user:self", name: "本人数据", group: "业务办理" },
] as const satisfies readonly PermissionDefinition[];

export type PermissionCode = (typeof PERMISSIONS)[number]["code"];

export const PERMISSION_CODES = new Set<string>(PERMISSIONS.map((p) => p.code));

export interface BuiltInRoleDefinition {
  code: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
}

const ADMIN_MENU = [
  "menu:admin_users",
  "menu:admin_personnel",
  "menu:admin_access",
  "menu:admin_master_data",
  "menu:admin_basic_data",
  "menu:admin_label_settings",
  "menu:admin_code_rules",
  "menu:admin_source_whitelist",
  "menu:admin_special_actions",
];

const ADMIN_ACTIONS = [
  "admin:access:manage",
  "admin:users:manage",
  "admin:personnel:manage",
  "admin:master-data:manage",
  "admin:basic-data:manage",
  "admin:code-rules:manage",
  "admin:label-settings:manage",
  "admin:source-whitelist:manage",
  "admin:special:manage",
  "admin:audit:read",
  "admin:system:read",
];

const USER_BASE = [
  "menu:dashboard",
  "menu:requests",
  "menu:assets",
  "menu:reminders",
  "asset:view_self",
  "user:self",
  "request:asset:requisition",
  "request:asset:replacement",
  "request:asset:return",
  "request:asset:repair",
  "request:asset:loss",
  "request:asset:borrow",
  "request:software:seat",
  "request:software:renewal",
];

const ASSET_ADMIN = [
  "menu:dashboard",
  "menu:materials",
  "menu:assets",
  "menu:accessories",
  "menu:licenses",
  "menu:requests",
  "menu:handovers",
  "menu:inventory",
  "menu:label_tasks",
  "menu:maintenance",
  "menu:reports",
  "menu:audit",
  "menu:reminders",
  "menu:attachments",
  "asset:manage",
  "asset:view_all",
  "asset:accept",
  "asset:dispose",
  "material:manage",
  "taxonomy:manage",
  "inventory:manage",
  "stock:correct",
  "request:manage",
  "operation:manage",
  "license:manage",
  "license:seat:manage",
  "license:renew",
  "label:manage",
  "report:read",
  "reminder:read",
  "audit:read",
  "attachment:manage",
];

export const BUILT_IN_ROLES: BuiltInRoleDefinition[] = [
  {
    code: "system_admin",
    name: "系统管理员",
    description:
      "系统配置、用户/人员/权限、主数据、编号、审计与特殊情况处理；不自动获得设备业务办理权。",
    isSystem: true,
    permissions: [
      ...ADMIN_MENU,
      ...ADMIN_ACTIONS,
      "menu:dashboard",
      "menu:audit",
      "menu:reports",
      "menu:reminders",
      "menu:attachments",
      "report:read",
      "reminder:read",
      "audit:read",
      "attachment:manage",
      "stock:correct",
      "request:approve_on_behalf",
    ],
  },
  {
    code: "asset_admin",
    name: "电子设备管理员",
    description: "在授权公司和成本中心范围内办理物料、库存、资产、低值易耗、软件许可与业务。",
    isSystem: true,
    permissions: ASSET_ADMIN,
  },
  {
    code: "user",
    name: "普通用户",
    description: "查看本人资产/席位，提交申领、借用、归还、报修、报失、席位申请，确认或拒绝交接。",
    isSystem: true,
    permissions: USER_BASE,
  },
  {
    code: "cost_center_owner",
    name: "成本中心负责人",
    description: "普通用户能力 + 审批本成本中心资产/软件/维修成本。",
    isSystem: true,
    permissions: [
      ...USER_BASE,
      "menu:approvals",
      "menu:maintenance",
      "request:approve",
      "maintenance:approve",
    ],
  },
  {
    code: "finance_asset_admin",
    name: "财务部固定资产管理员",
    description: "普通用户能力 + 查看全局设备状态。",
    isSystem: true,
    permissions: [...USER_BASE, "asset:view_all", "menu:reports", "report:read"],
  },
  {
    code: "finance_head",
    name: "财务部负责人",
    description: "普通用户能力 + 查看全局设备状态。",
    isSystem: true,
    permissions: [...USER_BASE, "asset:view_all", "menu:reports", "report:read"],
  },
  {
    code: "ceo",
    name: "董事长/CEO",
    description: "普通用户能力 + 全局设备查看 + 申领终局审批。",
    isSystem: true,
    permissions: [
      ...USER_BASE,
      "asset:view_all",
      "menu:approvals",
      "menu:reports",
      "report:read",
      "request:approve",
    ],
  },
];

export function hasPermission(permissions: readonly string[], required: string): boolean {
  if (permissions.includes("*") || permissions.includes(required)) return true;
  // Support "admin:*" style grants without making every check a prefix scan.
  if (permissions.includes(`${required.split(":")[0]}:*`)) return true;
  return false;
}

export function effectivePermissions(rolePermissions: readonly (readonly string[])[]): string[] {
  return [...new Set(rolePermissions.flat())];
}
